import { Component, ViewContainerRef, ViewChild, ElementRef } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, AbstractControl, ValidationErrors } from '@angular/forms';
import { PhotoService } from '../_services/photo.service';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';
import { SelfieCamFilterGeneratorComponent } from './selfie-cam-filter-generator.component';
import { LightShowTrack } from '../_models/LightShowTrack';
import { SelfieCamService, AddSelfieCamTo, Filter, SelfieCamListView, SelfieCamFormData, SelfieCamFileType, SelfieCamServiceFileCollection } from '../_services/selfie-cam.service';
import { DialogService } from '../_services/dialog.service';
import { ActivatedRoute, Router } from '@angular/router';
import { PaymentService } from '../_services/payment-service.service';
import { LightShowService, LightShowListView } from '../_services/light-show.service';

import { map } from 'rxjs/operators';
import { Observable, forkJoin, of } from 'rxjs';

import { NgxSpinnerService } from 'ngx-spinner';

import * as moment from 'moment';
import { DomSanitizer } from '@angular/platform-browser';

import { LightShowTrackService } from '../_services/track.service';
import { Segment } from '../mp3-slider/mp3-player.component';
import { UserService } from '../_services/user.service';
import { BaseCueServiceComponent as CueServiceComponentBase } from '../_shared/base.cueservice.component';
import { AuthService } from '../_services/auth.service';
import { NgxIndexedDBService } from 'ngx-indexed-db';

@Component({
  templateUrl: './selfie-cam.component.html',
  styleUrls: ['./selfie-cam.component.css']
})
export class SelfieCamComponent extends CueServiceComponentBase<SelfieCamFormData, SelfieCamListView> {
  private bsModalRef: BsModalRef;
  public loadingMp3: boolean;
  public loadingSelfieBottomImg: boolean;
  public tracks: LightShowTrack[];
  public segment: Segment;
  public showServicePeriod: boolean;
  private validSubscription = false;
  public hasPhotos = false;
  public serviceFiles: SelfieCamServiceFileCollection = new SelfieCamServiceFileCollection();


  // TODO: replace this with upgrade to trackService so that it manages the temp url and upload (like photoService)
  private mp3FileName: string;
  private mp3File: File;
  private mp3FileUrl: string;

  private _hasSiteManagement = false;
  get hasSiteManagement() {
    return this._hasSiteManagement;
  }

  get filters(): FormArray {
    return <FormArray>this.form.get('filters');
  }

  public get currentFilter(): Filter {
    if (this.selectedFilterIndex === -1) {
      return null;
    } else {
      return this.form.controls.filters.value[this.selectedFilterIndex];
    }
  }

  public get selectedFilterIndex(): number {
    return this.form.controls.selectedFilterIndex.value;
  }
  public set selectedFilterIndex(v: number) {
    this.form.patchValue({ selectedFilterIndex: v });
    this.form.markAsDirty();
  }

  // #region base class implementation
  constructor(
    photoService: PhotoService,
    private fb: FormBuilder,
    private modalService: BsModalService,
    private trackService: LightShowTrackService,
    private lightShowService: LightShowService,
    private sanitizer: DomSanitizer,
    viewRef: ViewContainerRef,
    dialogService: DialogService,
    selfieCamService: SelfieCamService,
    route: ActivatedRoute,
    router: Router,
    paymentService: PaymentService,
    spinner: NgxSpinnerService,
    userService: UserService,
    dbService: NgxIndexedDBService
  ) {
    super(selfieCamService, photoService, router, route, spinner, paymentService, dialogService, viewRef, userService, dbService);

    modalService.onHidden.subscribe(() => {
      try {
        if (this.bsModalRef) {
          this.filterCreationDialogClosed();
          this.bsModalRef = null;
        }
      } catch (err) {
        console.log(err);
      }
    });
  }

  // starting data for creating a new selfie cam
  protected getInitialDataForNewItem(): Observable<SelfieCamFormData> {
    return of(<SelfieCamFormData>{
      embedPosition: 0,
      embedMusic: false,
      shareText: '#sharetext',
      filterJson: '[]',
      bottomImageUrl: 'https://generic-filters.s3.amazonaws.com/countdown-smile.png',
      duration: 15,
      titleText: 'Synchronized selfie',
      name: 'New selfie cam',
      startDate: new Date().toString(),
      endDate: moment()
        .add(30, 'days')
        .toDate()
        .toString(),
      insertionPattern: '',
      useAudible: false
    });
  }

  // the route
  protected loadLookupData(): Observable<any> {
    const selfieCamPhotosObs = this.photoService.getSelfieCamPhotos(this.id).pipe(map(p => p.length > 0 ? this.hasPhotos = true : this.hasPhotos = false));
    const siteManagementObs = this.userService.hasSinglePermission('SiteManagement').pipe(map(sm => this._hasSiteManagement = sm));

    return forkJoin([selfieCamPhotosObs, siteManagementObs]);
  }

  protected getFormData(form: FormGroup, includeServiceFiles: boolean = false): SelfieCamFormData {

    // ensure that all safe urls that are left from errors are removed before uploading
    const filters = <Filter[]>JSON.parse(JSON.stringify(form.get('filters').value))
      .filter(f => f.url && !f.url.changingThisBreaksApplicationSecurity);

    const data = {
      bottomImageUrl: form.get('bottomImageUrl').value,
      duration: form.get('segments.0.duration').value,
      filterJson: JSON.stringify(filters),
      titleText: form.get('titleText').value,
      shareText: form.get('shareText').value,
      name: form.get('name').value,
      startDate: this.formatDate(form.get('period').value.startDate),
      endDate: this.formatDate(form.get('period').value.endDate),
      selectedFilterIndex: form.get('selectedFilterIndex').value,
      embedMusic: form.get('embedMusic').value,
      embedMp3Url: form.get('embedMp3Url').value,
      embedPosition: form.get('segments.0.position').value,
      couponId: form.get('couponId').value,
      forwardingTitle: form.get('forwardingTitle').value,
      forwardingBody: form.get('forwardingBody').value,
      regenerateAudio: form.get('regenerateAudio').value,
      insertionPattern: form.get('insertionPattern').value,
      useAudible: form.get('useAudible').value,
      dateCreated: new Date(Date.now()),
      serviceFiles: includeServiceFiles ? this.serviceFiles : null
    };

    if (!data.embedMusic) {

      data.embedMp3Url = null;
    }

    // q: is this still needed? [Antonio, 2019-02-26]
    // temp: safari CORS hack, check out if the following SO thread solves it https://stackoverflow.com/questions/16824661/cors-request-not-working-in-safari
    if (data.bottomImageUrl === 'assets/app/media/img/misc/smile.png') {
      data.bottomImageUrl = 'https://generic-filters.s3.amazonaws.com/countdown-smile.png';
    }

    return data as SelfieCamFormData;
  }

  protected setFormData(data: SelfieCamFormData, userSubscription: any, savedData?: SelfieCamFormData): FormGroup {

    const selfieCam = data;

    const period = { startDate: new Date(selfieCam.startDate), endDate: new Date(selfieCam.endDate) };
    const filters: Filter[] = <Filter[]>JSON.parse(<string>selfieCam.filterJson);

    // q: is this still needed? [Antonio, 2019-02-26]
    // temp: safari CORS hack, check out if the following SO thread solves it https://stackoverflow.com/questions/16824661/cors-request-not-working-in-safari
    if (selfieCam.bottomImageUrl === 'https://generic-filters.s3.amazonaws.com/countdown-smile.png') {
      selfieCam.bottomImageUrl = 'assets/app/media/img/misc/smile.png';
    }

    const form = this.fb.group({
      name: [selfieCam.name, [Validators.required, Validators.maxLength(160)]],
      couponCode: '',
      period: [period],
      titleText: [selfieCam.titleText, Validators.required],
      bottomImageUrl: [selfieCam.bottomImageUrl, Validators.required],
      filters: this.fb.array(filters.map(f => this.fb.group(f)), this.minLengthArray(3)),
      selectedFilterIndex: [selfieCam.selectedFilterIndex, Validators.min(0)],
      shareText: [selfieCam.shareText, [Validators.required, Validators.maxLength(160)]],
      embedMusic: selfieCam.embedMusic,
      regenerateAudio: [false],
      embedMp3Url: [selfieCam.embedMp3Url, this.requiredIfEmbedMusic(AddSelfieCamTo.customMp3)],
      segments: this.fb.array([
        this.fb.group({
          color: 'lightskyblue',
          position: selfieCam.embedPosition,
          duration: [+selfieCam.duration.toFixed(1), [Validators.pattern('(d|.)+'), this.validateDuration]]
        })
      ]),
      couponId: [selfieCam.couponId],
      forwardingTitle: [selfieCam.forwardingTitle],
      forwardingBody: [selfieCam.forwardingBody],
      insertionPattern: [selfieCam.insertionPattern, Validators.pattern(/^\s*((-\d)+|(\d)+)\s*(\s*,\s*((-\d)+|(\d)+))*\s*$/)],
      useAudible: [selfieCam.useAudible]
    });

    form.controls.embedMusic.valueChanges.subscribe(() => {
      form.get('embedMp3Url').updateValueAndValidity();
    });

    if (userSubscription.subscriptionStartDate && userSubscription.subscriptionEndDate) {
      form.controls.period.patchValue({
        startDate: new Date(userSubscription.subscriptionStartDate),
        endDate: new Date(userSubscription.subscriptionEndDate)
      });
      form.controls.period.disable();
      this.showServicePeriod = false;
    } else {
      this.showServicePeriod = true;
    }

    return form;
  }

  protected uploadContent(): Observable<any> {
    return this.mp3File
      ? this.trackService.uploadTrack(this.mp3File, false).pipe(map(res => {
          URL.revokeObjectURL(this.mp3FileUrl);
          this.form.patchValue({ embedMp3Url: res.trackUrl });
        }))
      : of(null);
  }

  protected onBuildSuccess(): string {
    // saving can attach a selfiecam to a lightshow which modifies the lightshow, so we must clear cache
    this.lightShowService.ClearCache();
    return super.onBuildSuccess();
  }
  // #endregion

  createUniqueFilterName(fileName: string): string {

    if (!fileName) {

      fileName = "filter";
    }

    const nameBase = fileName.substring(0, fileName.lastIndexOf('.')).substr(0, 10);
    let nameUnique = nameBase;
    const nameExists = n => (<Filter[]>this.filters.value).find(f => f.name === n);
    for (let i = 2; nameExists(nameUnique); i++) {
      nameUnique = `${nameBase.substr(0, 10 - i.toString().length - 1)}_${i}`;
    }
    return nameUnique;
  }

  addFilter(file: File) {

    if (file.type !== "image/jpeg" && file.type !== "image/png") {
      return
    }

    this.serviceFiles.files.push({ file, fileType: SelfieCamFileType.Filter });

    const uniqueName = this.createUniqueFilterName(file.name);
    const itemFG = this.fb.group({ name: uniqueName, url: '' });
    this.filters.push(itemFG);
    this.selectedFilterIndex = 0;
    this.photoService.enqueueUploadPhoto(file, uniqueName, { height: 1350, width: 1080 }, true, false).subscribe(res => {
      itemFG.patchValue({ url: res.url });
      itemFG.markAsDirty();
    });
  }

  couponChange() {
    this.form.controls.forwardingTitle.updateValueAndValidity();
    this.form.controls.forwardingBody.updateValueAndValidity();
  }

  addFilters(files: FileList) {
    for (let i = 0; i < files.length; i++) {
      this.addFilter(files[i]);
    }
  }

  deleteFilter(index: number) {
    if (index < this.selectedFilterIndex) {
      this.selectedFilterIndex--;
    } else if (index === this.selectedFilterIndex) {
      this.prevFilter();
    }

    const filterFiles = this.serviceFiles.files.filter(f => f.fileType === SelfieCamFileType.Filter);
    const filter = filterFiles[index];
    const filterIndex = this.serviceFiles.files.indexOf(filter);

    if (filterIndex > -1) {

      this.serviceFiles.files.splice(filterIndex, 1);
    }

    this.filters.removeAt(index);
    this.filters.markAsDirty();
    if (this.filters.length === 0) {
      this.selectedFilterIndex = -1;
    }
  }

  selectFilter(index: number) {
    this.selectedFilterIndex = index;
  }

  nextFilter() {
    if (this.filters.length === 0) {
      this.selectedFilterIndex = -1;
    } else {
      this.selectedFilterIndex = (this.selectedFilterIndex + 1) % this.filters.length;
    }
  }

  prevFilter() {
    if (this.filters.length === 0) {
      this.selectedFilterIndex = -1;
    } else {
      this.selectedFilterIndex = (this.selectedFilterIndex - 1 + this.filters.length) % this.filters.length;
    }
  }

  onEmbedMusicChanged() {
    this.form.controls.embedMp3Url.clearValidators();
    this.form.controls.embedMp3Url.updateValueAndValidity();

    if (this.form.controls.embedMusic.value) {

      this.onSelectMp3(false);
    }
  }

  onSelectMp3(manual: boolean = true) {
    if (!manual) {
      this.form.markAsDirty();
    }
    this.form.controls.embedMp3Url.setValidators([Validators.required]);
    this.form.controls.embedMp3Url.updateValueAndValidity();
  }

  public bottomImgEntered(files: File[]) {
    if (files.length > 0) {
      const file = files[0];

      this.serviceFiles.addBottomImageFile(file);

      this.loadingSelfieBottomImg = true;
      this.photoService.enqueueUploadPhoto(file, 'bottom image', { height: 300, width: 1080 }, false, false).subscribe(x => {
        this.form.patchValue({ bottomImageUrl: x.url });
        this.form.markAsDirty();
        this.loadingSelfieBottomImg = false;
      });
    }
  }

  public uploadMp3(files: File[]) {
    if (files.length > 0) {

      this.mp3File = files[0];

      this.serviceFiles.addAudio(this.mp3File);

      this.mp3FileUrl = URL.createObjectURL(this.mp3File);
      this.form.patchValue({ embedMp3Url: <any>this.sanitizer.bypassSecurityTrustUrl(this.mp3FileUrl) });
      this.form.get('embedMp3Url').markAsDirty();
    }
  }

  public openFilterCreationDialog() {
    this.bsModalRef = this.modalService.show(SelfieCamFilterGeneratorComponent, {
      class: 'generate-filter-modal-root modal-lg',
      ignoreBackdropClick: true
    });
  }

  public filterCreationDialogClosed() {
    if (this.bsModalRef.content.file) {
      this.addFilter(this.bsModalRef.content.file);
    }
  }

  protected loadServiceFiles(data: SelfieCamFormData) {

    for (let i = 0; i < data.serviceFiles?.files.length; i++) {

      switch(data.serviceFiles.files[i].fileType) {

        case SelfieCamFileType.Image:
          this.bottomImgEntered([data.serviceFiles.files[i].file]);
          break;
        
        case SelfieCamFileType.Audio:
          this.uploadMp3([data.serviceFiles.files[i].file]);
          break;

        case SelfieCamFileType.Filter:
          this.addFilter(data.serviceFiles.files[i].file)
          break;

        default:
          break;
      }
    }
  }

  private requiredIfEmbedMusic(mode: AddSelfieCamTo) {
    return (c: AbstractControl): ValidationErrors => {
      if (c.parent) {
        const form = c.root as FormGroup;
        if (form.controls.embedMusic.value) {
          return Validators.required(c);
        }
      }
      return null;
    };
  }

  private validateDuration(c: AbstractControl): ValidationErrors {
    if (c.parent) {
      const form = c.root as FormGroup;
      if (form.controls.embedMusic.value) {
        return Validators.min(15)(c) || Validators.max(500)(c);
      }
    }
    return null;
  }

  private formatDate(date: Date): string {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }

  public cancel() {
    this.navigateUp();
  }

  viewSelfies(id, serviceId) {
    this.router.navigate(['view'], {relativeTo: this.route});
  }

  minLengthArray(min: number) {
    return (c: AbstractControl): { [key: string]: any } => {
      if (c.value.length >= min) {
        return null;
      }

      return { minLengthArray: min };
    };
  }
}
